import React, { useCallback, useState } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { Layout, MainContent } from './BodyLayout.styles';
import { MainRoutes } from '../../common/mainRoutes/MainRoutes';
import {
  getWorkflowTypeFromDisplayName,
  PaymentWorkflowType,
  requiresClientMatterData,
} from '../../../components/container/forms/types/api/graph.types';
import { UseAuth } from '../../../contexts/AuthContext';
import { useFormContext } from '../../../contexts/useFormContext';
import { cancelWorkflow } from '../../../apis/workflow/post/cancelWorkflow';
import { createLogger } from '../../../utils/logger';
import { useFormConfiguration } from '../../container/forms/hooks/core/useFormConfiguration';
import { useClientMatterSearch } from '../../../hooks/useClientMatterSearch';
import { useWorkflowCreation } from '../../../hooks/useWorkflowCreation';

// Custom hooks
import { usePageState } from '../../../hooks/usePageState';
import {
  useNavigationItems,
  NavigationItem,
} from '../../../hooks/useNavigationItems';
import { useWorkflowPermissions } from '../../../hooks/useWorkflowPermissions';
import { useMobileRestriction } from '../../../hooks/useMobileRestriction';

// Components
import { ProgressBarManager } from './ProgressBarManager';
import { ModalManager } from './ModalManager';
import { SidebarManager } from './SidebarManager';
import { MobileRestrictionMessage } from './MobileRestrictionMessage';

// ============= Types =============
export type PermissionEntry = {
  permission: any; // eslint-disable-line @typescript-eslint/no-explicit-any
  displayName: string;
  workflowType: string | null;
  workflowTypeDisplayName: string | null;
};

// ============= Component =============
/**
 * BodyLayout Component
 * Main layout component that handles the application's structure including sidebars,
 * main content, and modals
 */
export const BodyLayout: React.FC = () => {
  // ============= Logger =============
  const logger = createLogger('BodyLayout');

  // ============= State =============
  const [isCancelling, setIsCancelling] = useState(false);
  const [isCancelModalOpen, setIsCancelModalOpen] = useState(false);
  const [isLeftSidebarOpen, setIsLeftSidebarOpen] = useState(false);
  const [isReassignOpen, setIsReassignOpen] = useState(false);

  // ============= Hooks =============
  const location = useLocation();
  const navigate = useNavigate();
  const { sectionProgress, formState, updateFormData } = useFormContext();
  const { isModalOpen, setIsModalOpen, createRequest, resetModalState } =
    useWorkflowCreation();
  const { getAccessToken, spoofedUser, currentUser } = UseAuth();

  // Custom hooks
  const { pageState } = usePageState(location);
  const navigationItems: NavigationItem[] = useNavigationItems(
    pageState,
    location
  );
  const { allowedWorkflowTypeEnums } = useWorkflowPermissions(currentUser);
  const { isRestrictedOnMobile } = useMobileRestriction(pageState);

  // Form configuration
  const { progressSection } = useFormConfiguration();

  const {
    clientData,
    matterData,
    selectedClientUno,
    selectedMatterUno,
    selectedClientLabel,
    handleClientSearch,
    handleMatterSearch,
    handleClientSelect,
    handleMatterSelect,
    isLoadingClients,
    isLoadingMatters,
  } = useClientMatterSearch(formState.office);

  // ============= Computed Values =============
  const showLeftSidebar =
    pageState.isRequestDetailsPage ||
    pageState.isActivitiesPage ||
    pageState.isFilesPage;

  // ============= Handlers =============
  const handleCreateRequest = useCallback(
    async (selectedWorkflowType?: PaymentWorkflowType) => {
      const workflowType =
        selectedWorkflowType ||
        (pageState.currentWorkflow?.name
          ? getWorkflowTypeFromDisplayName(pageState.currentWorkflow.name)
          : undefined);

        if (
          workflowType !== undefined &&
          workflowType !== PaymentWorkflowType.Undefined
        ) {
          if (!requiresClientMatterData(workflowType)) {
            await createRequest('', '', workflowType);
          } else if (selectedClientUno && selectedMatterUno) {
            await createRequest(
              selectedClientUno,
              selectedMatterUno,
              workflowType
            );
          }
        }
    },
    [
      createRequest,
      selectedClientUno,
      selectedMatterUno,
      pageState.currentWorkflow?.name,
    ]
  );

  const handleCancelWorkflow = useCallback(async () => {
    if (!formState.data?.id) {
      logger.error('Cannot cancel workflow: no workflow ID found');
      return;
    }

    setIsCancelling(true);

    try {
      const response = await cancelWorkflow({
        id: formState.data.id as number,
        getAccessToken,
        spoofedUser: spoofedUser?.value || null,
      });

      if (response.success) {
        logger.debug('Workflow canceled successfully');
        navigate('/');
      } else {
        logger.error('Failed to cancel workflow', { error: response.error });
      }
    } catch (error) {
      logger.error('Error cancelling workflow', { error });
    } finally {
      setIsCancelling(false);
      setIsCancelModalOpen(false);
    }
  }, [formState.data?.id, getAccessToken, spoofedUser, navigate, logger]);

  const handleCancelModalOpen = useCallback(() => {
    setIsCancelModalOpen(true);
  }, []);

  const handleCancelModalClose = useCallback(() => {
    setIsCancelModalOpen(false);
  }, []);

  const handleModalOpen = useCallback(() => {
    setIsModalOpen(true);
  }, [setIsModalOpen]);

  const handleModalClose = useCallback(() => {
    resetModalState();
    setIsModalOpen(false);
  }, [resetModalState, setIsModalOpen]);

  const handleOfficeChange = useCallback(
    (office: string) => {
      logger.debug('Office changed', { office });
      updateFormData({ office });
    },
    [updateFormData, logger]
  );

  const handleBackToWorkflows = useCallback(() => {
    const path = location.pathname;

    // From request page: go back to workflows
    if (path.startsWith('/request/')) {
      const parts = path.split('/');
      if (parts.length > 3) {
        const workflowKey = parts[2];
        navigate(`/workflows/${workflowKey}`);
        return;
      }
    }

    // From sub-pages (files/activities): go back to request page
    if (path.startsWith('/activities/') || path.startsWith('/files/')) {
      const parts = path.split('/');
      if (parts.length > 3) {
        const workflowType = parts[2];
        const id = parts[3];
        navigate(`/request/${workflowType}/${id}`);
        return;
      }
    }

    // Fallback to dashboard
    navigate('/');
  }, [location.pathname, navigate]);

  const handleReassignOpen = useCallback(() => {
    setIsReassignOpen(true);
  }, []);

  const handleReassignClose = useCallback(() => {
    setIsReassignOpen(false);
  }, []);

  const handleSidebarToggle = useCallback(() => {
    setIsLeftSidebarOpen(!isLeftSidebarOpen);
  }, [isLeftSidebarOpen]);

  // Wrapper functions to match CreateRequestModal's expected signatures
  const handleMatterSearchWrapper = useCallback(
    (query: string) => {
      handleMatterSearch(query);
    },
    [handleMatterSearch]
  );

  const handleClientSelectWrapper = useCallback(
    (option: string) => {
      handleClientSelect(option);
    },
    [handleClientSelect]
  );

  const handleMatterSelectWrapper = useCallback(
    (option: string) => {
      handleMatterSelect(option);
    },
    [handleMatterSelect]
  );

  // ============= Computed Values =============
  const showWorkflowTypeSelect =
    pageState.isDashboardPage && allowedWorkflowTypeEnums.length > 1;

  return (
    <Layout>
      <SidebarManager
        showLeftSidebar={showLeftSidebar}
        isLeftSidebarOpen={isLeftSidebarOpen}
        onSidebarToggle={handleSidebarToggle}
        navigationItems={navigationItems}
      />

      <MainContent $leftOpen={showLeftSidebar && isLeftSidebarOpen}>
        <ProgressBarManager
          sectionProgress={sectionProgress}
          pageState={pageState}
          progressSection={progressSection}
          formState={formState}
          currentUser={currentUser}
          isCancelling={isCancelling}
          onBackToWorkflows={handleBackToWorkflows}
          onReassign={handleReassignOpen}
          onCancelModalOpen={handleCancelModalOpen}
          onModalOpen={handleModalOpen}
          onCreateRequest={handleCreateRequest}
          allowedWorkflowTypeEnums={allowedWorkflowTypeEnums}
          getAccessToken={getAccessToken}
          spoofedUser={spoofedUser?.value || null}
        />

        {isRestrictedOnMobile ? <MobileRestrictionMessage /> : <MainRoutes />}
      </MainContent>

      <ModalManager
        isReassignOpen={isReassignOpen}
        onReassignClose={handleReassignClose}
        isCancelModalOpen={isCancelModalOpen}
        onCancelModalClose={handleCancelModalClose}
        onCancelWorkflow={handleCancelWorkflow}
        isCancelling={isCancelling}
        isModalOpen={isModalOpen}
        onModalClose={handleModalClose}
        onCreateRequest={handleCreateRequest}
        clientData={clientData}
        matterData={matterData}
        selectedClientLabel={selectedClientLabel}
        onClientSearch={handleClientSearch}
        onMatterSearch={handleMatterSearchWrapper}
        onClientSelect={handleClientSelectWrapper}
        onMatterSelect={handleMatterSelectWrapper}
        isLoadingClients={isLoadingClients}
        isLoadingMatters={isLoadingMatters}
        showWorkflowTypeSelect={showWorkflowTypeSelect}
        currentWorkflow={pageState.currentWorkflow}
        allowedWorkflowTypes={allowedWorkflowTypeEnums}
        isDashboardMode={pageState.isDashboardPage}
        onOfficeChange={handleOfficeChange}
      />
    </Layout>
  );
};
